import type * as Blockly from 'blockly/core';
import { BOARD_CONFIG, type BoardType } from '@/(routers)/boards/editor/block/blockConfig';
import type { ColorData } from '@/(routers)/boards/editor/block/definitions/fields/fieldColorSequence';
import { resolveBoard, stmt } from '@/(routers)/boards/editor/block/generators/arduino/_gen';

type Position = keyof (typeof BOARD_CONFIG)[BoardType]['pins'];

const POSITION_CHANNEL_BASE: Record<Position, number> = {
    UPPER_LEFT: 0,
    UPPER_RIGHT: 3,
    LOWER_LEFT: 6,
    LOWER_RIGHT: 9,
};

const FADE_STEP_DELAY = 10;

let _rgbLedUsed = false;
const _rgbLedPositions = new Set<Position>();

export function resetRgbLed(): void {
    _rgbLedUsed = false;
    _rgbLedPositions.clear();
}

export function rgbLedSetupCodes(board: BoardType): string {
    if (!_rgbLedUsed) return '';
    const lines: string[] = [];
    for (const pos of _rgbLedPositions) {
        const pins = BOARD_CONFIG[board].pins[pos];
        if (board === 'blue') {
            const ch = POSITION_CHANNEL_BASE[pos];
            lines.push(`  ledcAttachChannel(${pins.first}, 12800, 8, ${ch});\n`);
            lines.push(`  ledcAttachChannel(${pins.second}, 12800, 8, ${ch + 1});\n`);
            lines.push(`  ledcAttachChannel(${pins.third}, 12800, 8, ${ch + 2});\n`);
        } else {
            lines.push(`  pinMode(${pins.first}, OUTPUT);\n`);
            lines.push(`  pinMode(${pins.second}, OUTPUT);\n`);
            lines.push(`  pinMode(${pins.third}, OUTPUT);\n`);
        }
    }
    return lines.join('');
}

export function rgbLedHelperCodes(board: BoardType): string {
    if (!_rgbLedUsed) return '';
    const fns: string[] = [];
    for (const pos of _rgbLedPositions) {
        const pins = BOARD_CONFIG[board].pins[pos];
        const suffix = `${pins.first}_${pins.second}_${pins.third}`;
        if (board === 'orange') {
            fns.push(
                `void setColor_${suffix}(int r, int g, int b) {\n` +
                    `  analogWrite(${pins.first}, 255 - r);\n` +
                    `  analogWrite(${pins.second}, 255 - g);\n` +
                    `  analogWrite(${pins.third}, 255 - b);\n` +
                    `}`,
            );
        } else {
            fns.push(
                `void setColor_${suffix}(int r, int g, int b) {\n` +
                    `  ledcWrite(${pins.first}, 255 - r);\n` +
                    `  ledcWrite(${pins.second}, 255 - g);\n` +
                    `  ledcWrite(${pins.third}, 255 - b);\n` +
                    `}`,
            );
        }
    }
    return fns.join('\n\n');
}

export function register(gen: any): void {
    gen.forBlock['rgb_led'] = function (block: Blockly.Block): string {
        const position = block.getFieldValue('POSITION') as Position;
        const colorsJson = block.getFieldValue('COLORS') ?? '[]';
        const board = resolveBoard(block);
        const pins = BOARD_CONFIG[board].pins[position];
        const suffix = `${pins.first}_${pins.second}_${pins.third}`;

        _rgbLedUsed = true;
        _rgbLedPositions.add(position);

        let colors: ColorData[] = [];
        try {
            colors = JSON.parse(colorsJson) as ColorData[];
        } catch {
            colors = [];
        }

        if (colors.length === 0) return '';

        let code = '';
        let prevR = 0;
        let prevG = 0;
        let prevB = 0;

        for (let i = 0; i < colors.length; i++) {
            const color = colors[i];
            const curR = color.enabled ? color.r : 0;
            const curG = color.enabled ? color.g : 0;
            const curB = color.enabled ? color.b : 0;

            if (i === 0) {
                code += stmt(`setColor_${suffix}(${curR}, ${curG}, ${curB})`);
            } else {
                code += `/* グラデーション: (${prevR},${prevG},${prevB}) -> (${curR},${curG},${curB}) */\n`;
                code +=
                    `for (int _fr = ${prevR}, _fg = ${prevG}, _fb = ${prevB};;) {\n` +
                    `  setColor_${suffix}(_fr, _fg, _fb);\n` +
                    `  delay(${FADE_STEP_DELAY});\n` +
                    `  if (_fr != ${curR}) _fr += (_fr < ${curR}) ? 1 : -1;\n` +
                    `  if (_fg != ${curG}) _fg += (_fg < ${curG}) ? 1 : -1;\n` +
                    `  if (_fb != ${curB}) _fb += (_fb < ${curB}) ? 1 : -1;\n` +
                    `  if (_fr == ${curR} && _fg == ${curG} && _fb == ${curB}) break;\n` +
                    `}\n`;
            }

            code += stmt(`delay(${color.duration ?? 1000})`);

            prevR = curR;
            prevG = curG;
            prevB = curB;
        }

        return code;
    };
}
